import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router';
import {
  ArrowLeft,
  FlaskConical,
  Play,
  AlertTriangle,
  Trophy,
  Brain,
  Check,
} from 'lucide-react';
import {
  useStrategy,
  useBacktestCompare,
  useApplySuggestedRules,
  type BacktestParams,
  type ComparativeResult,
} from '@/hooks/use-strategies';
import { useJob, BACKTEST_STAGES } from '@/hooks/use-job';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { Input } from '@/components/ui/input';
import { DatePicker } from '@/components/ui/date-picker';
import { Badge } from '@/components/ui/badge';
import { Modal } from '@/components/ui/modal';
import { formatPnl, formatPct, pnlColor, friendlyError } from '@/lib/format';
import { getMaxBacktestDays } from '@/lib/symbol-categories';
import type { BacktestReport } from '@/types/api';

export default function StrategyComparePage() {
  const { id } = useParams();
  const { data: strategy, isLoading: strategyLoading } = useStrategy(id!);
  const compare = useBacktestCompare();
  const [jobId, setJobId] = useState<string | null>(null);
  const job = useJob<ComparativeResult>('backtest', jobId);

  const snapshot = strategy?.analysis_snapshot as Record<string, unknown> | null;
  const snapshotCapital = snapshot?.suggestion_capital as number | undefined;
  const snapshotLeverage = snapshot?.suggestion_leverage as number | undefined;

  const [days, setDays] = useState(30);
  const [dateMode, setDateMode] = useState<'preset' | 'custom'>('preset');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [capital, setCapital] = useState(String(snapshotCapital ?? 10000));
  const [leverage, setLeverage] = useState(String(snapshotLeverage ?? 100));

  useEffect(() => {
    if (snapshotCapital != null) setCapital(String(snapshotCapital));
  }, [snapshotCapital]);
  useEffect(() => {
    if (snapshotLeverage != null) setLeverage(String(snapshotLeverage));
  }, [snapshotLeverage]);

  if (strategyLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!strategy) {
    return (
      <div className="glass-sm rounded-lg p-6 border-red/20 text-center">
        <p className="text-red text-sm font-semibold mb-2">Strategy not found</p>
        <Link to="/strategies" className="inline-block mt-3">
          <Button variant="ghost" size="sm">
            <ArrowLeft size={14} />
            Back
          </Button>
        </Link>
      </div>
    );
  }

  const stats = (snapshot?.stats ?? {}) as Record<string, unknown>;
  const symbol = typeof stats.symbol === 'string' ? stats.symbol : undefined;
  const rules = strategy.rules as Record<string, unknown> | null;

  const handleRun = async () => {
    const params: BacktestParams = {
      ...(dateMode === 'custom'
        ? { startDate, endDate: endDate || undefined }
        : { days }),
      capital: parseFloat(capital) || 10000,
      leverage: parseInt(leverage) || 1,
      symbol,
      timeframe: ((rules?.market_data as Record<string, unknown>)?.timeframe as string) ?? undefined,
    };
    try {
      const result = await compare.mutateAsync({ id: id!, params });
      setJobId(result.jobId);
    } catch { /* error handled by mutation */ }
  };

  const isProcessing = compare.isPending || job.isLoading;
  const progressStage = job.progress?.stage as string | undefined;
  const progressLabel = progressStage
    ? BACKTEST_STAGES[progressStage] || progressStage
    : 'Running comparison...';
  const compareResult = job.isComplete ? job.result : undefined;

  return (
    <div className="space-y-5">
      {/* Back Link */}
      <Link
        to={`/strategies/${id}`}
        className="inline-flex items-center gap-1.5 text-[10px] font-semibold text-ink-3 hover:text-orange transition-colors"
      >
        <ArrowLeft size={12} />
        Back to Strategy
      </Link>

      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-md bg-surface-2 flex items-center justify-center">
          <FlaskConical size={18} className="text-orange" />
        </div>
        <div>
          <h1 className="text-lg font-extrabold tracking-tight">3-Way Comparison</h1>
          <p className="text-xs text-ink-3">
            Compare original vs suggested vs current rules for {strategy.name}
          </p>
        </div>
      </div>

      {/* Config */}
      <div className="glass-sm rounded-lg p-4 space-y-3">
        <div className="flex gap-2 flex-wrap">
          {[7, 30, 365, 730].map((d) => {
            const maxDays = getMaxBacktestDays(symbol);
            const isDisabled = d > maxDays;
            return (
              <button
                key={d}
                onClick={() => { if (!isDisabled) { setDays(d); setDateMode('preset'); } }}
                disabled={isDisabled}
                title={isDisabled ? `Deriv synthetics only support up to ${maxDays} days of historical data` : undefined}
                className={`text-[10px] font-bold px-3 py-1.5 rounded-md transition-all ${
                  isDisabled
                    ? 'bg-surface text-ink-3/40 border border-glass-border cursor-not-allowed opacity-50'
                    : dateMode === 'preset' && days === d
                      ? 'bg-orange-bg text-orange border border-orange/20 cursor-pointer'
                      : 'bg-surface text-ink-3 border border-glass-border hover:bg-surface-2 cursor-pointer'
                }`}
              >
                {d === 7 ? '7d' : d === 30 ? '30d' : d === 365 ? '1y' : '2y'}
              </button>
            );
          })}
          <button
            onClick={() => setDateMode('custom')}
            className={`text-[10px] font-bold px-3 py-1.5 rounded-md transition-all cursor-pointer ${
              dateMode === 'custom'
                ? 'bg-orange-bg text-orange border border-orange/20'
                : 'bg-surface text-ink-3 border border-glass-border hover:bg-surface-2'
            }`}
          >
            Custom
          </button>
        </div>
        {dateMode === 'custom' && (
          <div className="flex gap-2">
            <div className="flex-1">
              <DatePicker label="Start" value={startDate} onChange={setStartDate} max={endDate || new Date().toISOString().slice(0, 10)} />
            </div>
            <div className="flex-1">
              <DatePicker label="End" value={endDate} onChange={setEndDate} min={startDate || undefined} max={new Date().toISOString().slice(0, 10)} />
            </div>
          </div>
        )}

        <div className="flex gap-3">
          <div className="flex-1">
            <Input
              label="Capital"
              type="number"
              value={capital}
              onChange={(e) => setCapital(e.target.value)}
            />
          </div>
          <div className="flex-1">
            <label className="block text-[10px] font-semibold text-ink-3 mb-1.5">Leverage</label>
            <select
              value={leverage}
              onChange={(e) => setLeverage(e.target.value)}
              className="w-full bg-surface border border-glass-border rounded-lg px-3 py-2 text-sm font-mono text-ink outline-none transition-all duration-200 focus:border-orange focus:shadow-[0_0_8px_rgba(249,115,22,0.15)] cursor-pointer appearance-none"
            >
              {[1, 50, 100, 200, 500, 1000, 2000].map((l) => (
                <option key={l} value={l}>1:{l}</option>
              ))}
            </select>
          </div>
        </div>

        <Button className="w-full" onClick={handleRun} disabled={isProcessing || (dateMode === 'custom' && (!startDate || !endDate))}>
          {isProcessing ? (
            <>
              <Spinner size="sm" />
              {progressLabel}
            </>
          ) : (
            <>
              <Play size={14} />
              Run 3-Way Comparison
            </>
          )}
        </Button>
        {dateMode === 'custom' && (!startDate || !endDate) && (
          <p className="text-[10px] text-amber font-semibold mt-1">Both dates are required</p>
        )}

        {(compare.isError || job.isFailed) && (
          <div className="flex items-center gap-2 bg-red-bg rounded-md px-3 py-2">
            <AlertTriangle size={12} className="text-red flex-shrink-0" />
            <p className="text-[10px] text-red font-semibold">
              {friendlyError(job.error ?? (compare.error as Error)?.message, 'Comparison failed')}
            </p>
          </div>
        )}

      </div>

      {/* Results — show spinner overlay on top of previous results */}
      {(isProcessing || compareResult) && (
        <div className={`relative ${isProcessing ? 'min-h-[120px]' : ''}`}>
          {isProcessing && (
            <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 bg-bg/70 rounded-lg backdrop-blur-sm">
              <Spinner size="md" />
              <p className="text-xs text-ink-4 font-semibold">{progressLabel}</p>
            </div>
          )}
          {compareResult && (
            <CompareResults
              result={compareResult}
              strategyId={strategy.id}
              strategyStatus={strategy.status}
              suggestionsApplied={!!snapshot?.suggestions_applied}
            />
          )}
        </div>
      )}
    </div>
  );
}

// ─── Compare Results ────────────────────────────────

function CompareResults({
  result,
  strategyId,
  strategyStatus,
  suggestionsApplied,
}: {
  result: ComparativeResult;
  strategyId: string;
  strategyStatus: string;
  suggestionsApplied: boolean;
}) {
  const [showApplyConfirm, setShowApplyConfirm] = useState(false);
  const applySuggested = useApplySuggestedRules();
  const hasSuggested = result.suggested && !('error' in result.suggested);
  const allColumns: {
    label: string;
    variant: 'original' | 'suggested' | 'current';
    report: BacktestReport | { error: string } | null;
    color: string;
  }[] = [
    { label: 'Original', variant: 'original' as const, report: result.original, color: 'text-blue' },
    { label: 'Suggested', variant: 'suggested' as const, report: result.suggested, color: 'text-violet' },
    { label: 'Current', variant: 'current' as const, report: result.current, color: 'text-orange' },
  ];
  const columns = allColumns.filter((c) => c.report != null);

  const bestBy = result.comparison?.bestBy;

  const metrics: {
    label: string;
    key: keyof BacktestReport;
    bestByKey?: keyof NonNullable<typeof bestBy>;
    format: (v: number) => string;
    higherIsBetter: boolean;
    colorize?: boolean;
  }[] = [
    { label: 'Net Profit', key: 'netProfit', format: (v) => formatPnl(v), higherIsBetter: true, colorize: true },
    { label: 'Return %', key: 'returnPct', bestByKey: 'returnPct', format: (v) => formatPct(v), higherIsBetter: true, colorize: true },
    { label: 'Win Rate', key: 'winRate', bestByKey: 'winRate', format: (v) => `${v.toFixed(1)}%`, higherIsBetter: true },
    { label: 'Max DD', key: 'maxDrawdown', bestByKey: 'maxDrawdown', format: (v) => `${v.toFixed(2)}%`, higherIsBetter: false },
    { label: 'Profit Factor', key: 'profitFactor', bestByKey: 'profitFactor', format: (v) => `${v.toFixed(2)}×`, higherIsBetter: true },
    { label: 'Sharpe', key: 'sharpeRatio', bestByKey: 'sharpeRatio', format: (v) => v.toFixed(2), higherIsBetter: true },
    { label: 'Total Trades', key: 'totalTrades', format: (v) => String(v), higherIsBetter: false },
  ];

  const getValues = (key: keyof BacktestReport) =>
    columns.map((c) => {
      if (!c.report || 'error' in c.report) return null;
      return c.report[key] as number;
    });

  return (
    <div className="space-y-3">
      {/* Rules identical notice */}
      {result.comparison?.rulesIdentical && (
        <div className="flex items-center gap-2 bg-surface-2 rounded-lg px-3 py-2">
          <Badge color="blue">Info</Badge>
          <span className="text-[10px] text-ink-3 font-semibold">
            Original and Current rules are identical — results are shared.
          </span>
        </div>
      )}

      <div className="glass-sm rounded-lg overflow-x-auto">
        {/* Column Headers */}
        <div
          className="grid gap-0 border-b border-border min-w-[500px]"
          style={{ gridTemplateColumns: `140px repeat(${columns.length}, 1fr)` }}
        >
          <div className="p-3" />
          {columns.map((col) => (
            <div key={col.label} className="p-3 text-center border-l border-border">
              <p className={`text-[10px] font-extrabold uppercase tracking-wider ${col.color}`}>
                {col.label}
              </p>
            </div>
          ))}
        </div>

        {/* Metric Rows */}
        {metrics.map((metric) => {
          const values = getValues(metric.key);
          const validValues = values.filter((v): v is number => v != null);
          const bestValue =
            validValues.length > 0
              ? metric.higherIsBetter
                ? Math.max(...validValues)
                : Math.min(...validValues)
              : null;

          return (
            <div
              key={metric.label}
              className="grid gap-0 border-b border-border last:border-b-0 min-w-[500px]"
              style={{ gridTemplateColumns: `140px repeat(${columns.length}, 1fr)` }}
            >
              <div className="p-3 flex items-center">
                <span className="text-[10px] font-semibold text-ink-3 uppercase tracking-wider">
                  {metric.label}
                </span>
              </div>
              {columns.map((col, i) => {
                const val = values[i];
                const isError = col.report && 'error' in col.report;
                // Use backend bestBy when available, fall back to local comparison
                const isBest = validValues.length > 1 && (
                  (metric.bestByKey && bestBy)
                    ? bestBy[metric.bestByKey] === col.variant
                    : val != null && val === bestValue
                );

                return (
                  <div
                    key={col.label}
                    className={`p-3 text-center border-l border-border ${isBest ? 'bg-green/5' : ''}`}
                  >
                    {isError ? (
                      <span className="text-[10px] text-red">Error</span>
                    ) : val != null ? (
                      <div className="flex items-center justify-center gap-1">
                        <span
                          className={`text-xs font-bold font-mono ${
                            metric.colorize ? pnlColor(val) : 'text-ink'
                          }`}
                        >
                          {metric.format(val)}
                        </span>
                        {isBest && <Trophy size={10} className="text-green" />}
                      </div>
                    ) : (
                      <span className="text-[10px] text-ink-4">—</span>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}

        {/* Error details */}
        {columns.some((c) => c.report && 'error' in c.report) && (
          <div className="p-3 border-t border-border space-y-1.5">
            {columns.map((col) => {
              if (!col.report || !('error' in col.report)) return null;
              const isUnavailable = col.report.error.includes('unavailable');
              return (
                <div key={col.label} className="flex items-start gap-2">
                  <Badge color={isUnavailable ? 'amber' : 'red'}>{col.label}</Badge>
                  <span className={`text-[10px] ${isUnavailable ? 'text-amber' : 'text-red'}`}>
                    {col.report.error}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Per-column Warnings */}
      {columns.some((c) => c.report && !('error' in c.report) && (c.report as BacktestReport).warnings?.length) && (
        <div className="glass-sm rounded-lg overflow-x-auto">
          <div
            className="grid gap-0 border-b border-border min-w-[500px]"
            style={{ gridTemplateColumns: `140px repeat(${columns.length}, 1fr)` }}
          >
            <div className="p-3 flex items-center">
              <span className="text-[10px] font-semibold text-ink-3 uppercase tracking-wider">
                Warnings
              </span>
            </div>
            {columns.map((col) => {
              const report = col.report && !('error' in col.report) ? (col.report as BacktestReport) : null;
              const warnings = report?.warnings ?? [];
              return (
                <div key={col.label} className="p-3 border-l border-border">
                  {warnings.length > 0 ? (
                    <div className="space-y-1.5">
                      {warnings.map((w, i) => (
                        <div
                          key={i}
                          className={`flex items-start gap-1.5 rounded-md px-2 py-1.5 ${
                            w.severity === 'CRITICAL'
                              ? 'bg-red-bg'
                              : w.severity === 'WARNING'
                                ? 'bg-amber-bg'
                                : 'bg-surface-2'
                          }`}
                        >
                          <AlertTriangle
                            size={9}
                            className={`flex-shrink-0 mt-0.5 ${
                              w.severity === 'CRITICAL'
                                ? 'text-red'
                                : w.severity === 'WARNING'
                                  ? 'text-amber'
                                  : 'text-ink-3'
                            }`}
                          />
                          <p
                            className={`text-[9px] font-semibold leading-tight ${
                              w.severity === 'CRITICAL'
                                ? 'text-red'
                                : w.severity === 'WARNING'
                                  ? 'text-amber'
                                  : 'text-ink-3'
                            }`}
                          >
                            {w.message}
                          </p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <span className="text-[10px] text-ink-4">No warnings</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Per-column Cost Analysis */}
      {columns.some((c) => c.report && !('error' in c.report) && (c.report as BacktestReport).costAnalysis) && (
        <div className="glass-sm rounded-lg overflow-x-auto">
          <div
            className="grid gap-0 border-b border-border min-w-[500px]"
            style={{ gridTemplateColumns: `140px repeat(${columns.length}, 1fr)` }}
          >
            <div className="p-3" />
            {columns.map((col) => (
              <div key={col.label} className="p-3 text-center border-l border-border">
                <p className={`text-[10px] font-extrabold uppercase tracking-wider ${col.color}`}>
                  {col.label}
                </p>
              </div>
            ))}
          </div>

          {/* Cost/Trade */}
          <div
            className="grid gap-0 border-b border-border min-w-[500px]"
            style={{ gridTemplateColumns: `140px repeat(${columns.length}, 1fr)` }}
          >
            <div className="p-3 flex items-center">
              <span className="text-[10px] font-semibold text-ink-3 uppercase tracking-wider">
                Cost/Trade
              </span>
            </div>
            {columns.map((col) => {
              const report = col.report && !('error' in col.report) ? (col.report as BacktestReport) : null;
              const cost = report?.costAnalysis;
              return (
                <div key={col.label} className="p-3 text-center border-l border-border">
                  {cost ? (
                    <span className="text-xs font-bold font-mono">
                      ${cost.roundTripCostPerTrade.toFixed(3)}
                    </span>
                  ) : (
                    <span className="text-[10px] text-ink-4">—</span>
                  )}
                </div>
              );
            })}
          </div>

          {/* Max Safe Levels */}
          <div
            className="grid gap-0 border-b border-border min-w-[500px]"
            style={{ gridTemplateColumns: `140px repeat(${columns.length}, 1fr)` }}
          >
            <div className="p-3 flex items-center">
              <span className="text-[10px] font-semibold text-ink-3 uppercase tracking-wider">
                Max Safe Levels
              </span>
            </div>
            {columns.map((col) => {
              const report = col.report && !('error' in col.report) ? (col.report as BacktestReport) : null;
              const cost = report?.costAnalysis;
              return (
                <div key={col.label} className="p-3 text-center border-l border-border">
                  {cost ? (
                    <span className="text-xs font-bold font-mono">{cost.maxSafeGridLevels}</span>
                  ) : (
                    <span className="text-[10px] text-ink-4">—</span>
                  )}
                </div>
              );
            })}
          </div>

          {/* Friction Ratio */}
          {columns.some((c) => {
            const r = c.report && !('error' in c.report) ? (c.report as BacktestReport) : null;
            return r?.costAnalysis?.frictionRatio != null;
          }) && (
            <div
              className="grid gap-0 border-b border-border last:border-b-0 min-w-[500px]"
              style={{ gridTemplateColumns: `140px repeat(${columns.length}, 1fr)` }}
            >
              <div className="p-3 flex items-center">
                <span className="text-[10px] font-semibold text-ink-3 uppercase tracking-wider">
                  Friction Ratio
                </span>
              </div>
              {columns.map((col) => {
                const report = col.report && !('error' in col.report) ? (col.report as BacktestReport) : null;
                const fr = report?.costAnalysis?.frictionRatio;
                return (
                  <div key={col.label} className="p-3 text-center border-l border-border">
                    {fr != null ? (
                      <span className={`text-xs font-bold font-mono ${fr > 0.5 ? 'text-red' : ''}`}>
                        {(fr * 100).toFixed(0)}%
                      </span>
                    ) : (
                      <span className="text-[10px] text-ink-4">—</span>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Min Recommended Capital */}
          <div
            className="grid gap-0 min-w-[500px]"
            style={{ gridTemplateColumns: `140px repeat(${columns.length}, 1fr)` }}
          >
            <div className="p-3 flex items-center">
              <span className="text-[10px] font-semibold text-ink-3 uppercase tracking-wider">
                Min Capital
              </span>
            </div>
            {columns.map((col) => {
              const report = col.report && !('error' in col.report) ? (col.report as BacktestReport) : null;
              const cost = report?.costAnalysis;
              return (
                <div key={col.label} className="p-3 text-center border-l border-border">
                  {cost ? (
                    <span className="text-xs font-bold font-mono">
                      ${cost.minRecommendedCapital.toFixed(0)}
                    </span>
                  ) : (
                    <span className="text-[10px] text-ink-4">—</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Apply Suggested Rules */}
      {hasSuggested && strategyStatus === 'DRAFT' && (
        <div className="flex flex-col items-center gap-2">
          {suggestionsApplied ? (
            <div className="flex items-center gap-2 text-green text-[10px] font-semibold">
              <Check size={12} />
              Suggested rules applied — currently active
            </div>
          ) : (
            <>
              {applySuggested.isError && (
                <div className="flex items-center gap-2 text-red text-[10px]">
                  <AlertTriangle size={10} />
                  <span>Failed to apply suggestions. Please try again.</span>
                </div>
              )}
              <Button size="sm" onClick={() => setShowApplyConfirm(true)}>
                <Brain size={12} />
                Apply Suggested Rules
              </Button>
            </>
          )}
        </div>
      )}

      <Modal
        open={showApplyConfirm}
        onClose={() => setShowApplyConfirm(false)}
        title="Apply AI Suggestions"
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={() => setShowApplyConfirm(false)}>
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={() => {
                applySuggested.mutate(strategyId, {
                  onSuccess: () => setShowApplyConfirm(false),
                });
              }}
              disabled={applySuggested.isPending}
            >
              {applySuggested.isPending ? (
                <>
                  <Spinner size="sm" />
                  Applying...
                </>
              ) : (
                <>
                  <Check size={12} />
                  Apply Changes
                </>
              )}
            </Button>
          </>
        }
      >
        <div className="space-y-2">
          <p className="text-xs text-ink-3">
            This will replace your current rules with the AI-suggested improvements.
          </p>
          <p className="text-xs text-ink-3">
            You can revert to the original rules anytime using{' '}
            <strong className="text-ink">Edit Config &rarr; Reset to Original</strong>.
          </p>
        </div>
      </Modal>
    </div>
  );
}
